import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { VdjPost, vdjBeatChroma } from './lib/vdjLook.js';
import { Adapt } from './lib/adapt.js';

// Contrat du rig partagé : interdit. La galaxie a sa propre projection.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'galaxy',
  family: 'particles',
  danceability: 0.66,
  requiresTier: 0,
  energyLevel: 0.50,
  bpmMin: 60,
  bpmMax: 160,
  movement: 0.50,
  tonality: 0.75,
  enabled: true
};

// Galaxy : une galaxie spirale de 80 000 étoiles vue de trois quarts, qui
// tourne autour du centre de l'écran. Visualizer UNIVERSEL (lib/adapt.js) :
// majestueuse au calme, vertigineuse à l'intensité, de l'ambient à la
// progressive.
//
// L'ONDE DE DENSITÉ (Kalnajs) : chaque étoile parcourt une orbite FERMÉE,
// un ovale à m lobes r(θ) = a (1 + e cos(m (θ - ω(a)))), dont l'orientation
// ω(a) = k a + Ωp t croît avec sa taille. Aucune étoile ne suit un bras : les
// bras sont les endroits où les ovales voisins se RESSERRENT (dr/da minimal).
// m = 2 est l'ellipse classique ; m = 3 ou 4 « dédouble » les ellipses. Le
// passage de m1 à m2 mélange les deux termes de déformation (uMix 0 -> 1 en
// 2 temps) : les bras se dédoublent en continu, sans qu'aucune étoile saute.
// e(a) = E / (1 + a m k) garde dr/da > 0 (aucun croisement d'orbites, donc
// une densité finie) : E règle la netteté, k l'enroulement.
// Les étoiles avancent sur leur ovale à Ω(a) = 0,9 / (a + 0,1) : les
// intérieures tournent plus vite. Tout est dans le vertex shader, à partir
// d'attributs statiques (a, phase, hauteur, graine) et d'une horloge
// intégrée (la musique module la VITESSE, jamais la position).
//
// LE RENDU : points de 1 à 3 px (R7), bulbe jaune-orangé, bras bleutés,
// quelques étoiles rouges ; les étoiles des zones denses un peu plus
// brillantes. POUSSIÈRE : sur le bord INTÉRIEUR des bras (là où la densité
// croît vers l'extérieur), les étoiles sont atténuées. NÉBULEUSES : 300
// grands sprites gaussiens (R14, nuls avant le bord, R12) posés sur la CRÊTE
// des bras, calculée analytiquement (ψ* = π + atan(a m k)), en additif léger.
// ACCUMULATION : une cible demi-flottante, decay = 0,5^(dt / 35 ms) (R15),
// seulement pour adoucir le scintillement des points fins.
//
// ADAPTATION (adapt.js) : intensity règle la vitesse (motion), l'enroulement
// et la netteté des bras, la saturation (deep -> vivid) ; pulse = une vague
// de lumière du centre vers l'extérieur sur chaque temps ; sur la mesure, les
// nébuleuses d'un bras s'allument (à l'intensité) ; séparation RGB vdj sur
// le temps (VdjPost) proportionnelle à pulse ; section -> 2 -> 3 -> 4 bras ;
// drop (seulement si dropReady) -> le bulbe flashe et une onde de choc
// traverse le disque (les étoiles s'écartent puis reviennent).
//
// LA CAMÉRA : inclinaison 10° -> 80° (moyenne 45°) sur 128 temps (60 s sans
// tempo), jamais de coupe ; le cadrage est recalculé à chaque frame pour que
// le disque entier tienne dans le cadre (R6), centré sur le centre de
// composition (R19). Le bulbe est sous la pochette si elle est centrée : le
// masque safeZone l'atténue.
//
// Debug : ?galaxyarms=2|3|4, ?galaxywind=<f> (tours par unité de rayon),
// ?adaptsim=<profil>, ?adaptshow=1.

const TIER = { 0: 20000, 1: 40000, 2: 80000 };
const NEBULAE = 300;
const HALF_LIFE = 0.012;              // 20-35 ms drew the fast inner stars as dashes                // the accumulation's half-life, seconds
const SAFE_FLOOR = 0.45;
const FALLBACK = [0x6a7dff, 0xff5fa8];
const FIT_R = 1.12;                     // the disc's outer radius kept in frame (+ the shock's push)

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
const clamp01 = (x) => Math.max(0, Math.min(1, x));
const smooth = (x) => { x = clamp01(x); return x * x * (3 - 2 * x); };

// the galaxy -> clip projection and the density-wave orbit, shared by the stars and the nebulae
const GALAXY_GLSL = `
  uniform float uTime, uPattern, uWind, uEcc, uM1, uM2, uMix;
  uniform float uTilt, uDist, uScale, uAspect;
  uniform vec2 uStage;
  uniform float uShockR, uShockAmp;
  // the m-lobed oval of semi-axis a at azimuth th: radius, and dr/da along a fixed azimuth
  float kWind() { return uWind * 6.2831853; }
  float eccAt(float a, float m) { return uEcc / (1.0 + a * m * kWind()); }
  float ovalR(float a, float th) {
    float rot = kWind() * a + uPattern;
    float e1 = eccAt(a, uM1), e2 = eccAt(a, uM2);
    return a * (1.0 + mix(e1 * cos(uM1 * (th - rot)), e2 * cos(uM2 * (th - rot)), uMix));
  }
  float drda(float a, float th) {
    float k = kWind(), rot = k * a + uPattern;
    float p1 = uM1 * (th - rot), p2 = uM2 * (th - rot);
    float e1 = eccAt(a, uM1), e2 = eccAt(a, uM2);
    float d1 = 1.0 + e1 * cos(p1) + a * e1 * uM1 * k * sin(p1);
    float d2 = 1.0 + e2 * cos(p2) + a * e2 * uM2 * k * sin(p2);
    return mix(d1, d2, uMix);
  }
  // the drop's shock: a ring pushing the stars outward as it passes
  float shock(float r) { float q = (r - uShockR) / 0.09; return uShockAmp * exp(-q * q); }
  vec4 project(vec3 g) {
    float ct = cos(uTilt), st = sin(uTilt);
    vec3 v = vec3(g.x, g.y * ct - g.z * st, g.y * st + g.z * ct);
    float w = uDist / max(uDist - v.z, 0.2);
    vec2 s = v.xy * w * uScale + uStage;
    return vec4(s.x / (0.5 * uAspect), s.y / 0.5, 0.0, 1.0);
  }
`;

const starVertex = `
  precision highp float;
  ${GALAXY_GLSL}
  attribute vec4 aStar;                 // a, phase, height (gaussian), seed
  uniform float uPxScale, uBright, uWaveR, uWaveAmp, uSat, uDeep;
  uniform vec3 uArmCol;
  varying vec3 vCol;
  void main() {
    float a = aStar.x, seed = aStar.w;
    float th = aStar.y + 0.9 / (a + 0.1) * uTime;          // inner stars faster
    float r = ovalR(a, th);
    float push = shock(r);
    r += push;
    // the bulge is a spheroid (height grows WITH the radius - a fixed height
    // on a near-zero radius drew a needle), the disc is thin
    float thick = mix(0.012, 0.75 * a, exp(-a * a / 0.015));
    vec3 g = vec3(r * cos(th), r * sin(th), aStar.z * thick);
    gl_Position = project(g);
    // density: 1/(dr/da); dust where it grows outward (the arm's inner edge)
    float d0 = drda(a, th), d1 = drda(a + 0.025, th);
    float crowd = 1.0 / max(d0, 0.05);
    float dust = smoothstep(0.02, 0.18, (d0 - d1)) * smoothstep(0.12, 0.3, a);
    // colour by radius: warm bulge, bluish arms, a few red stars
    vec3 bulge = vec3(1.0, 0.72, 0.38), arm = mix(vec3(0.55, 0.70, 1.0), uArmCol, 0.25);
    vec3 c = mix(bulge, arm, smoothstep(0.08, 0.32, a));
    if (fract(seed * 7.13) < 0.06) c = vec3(1.0, 0.35, 0.22);
    // deep: more saturated, darker
    float l = dot(c, vec3(0.3, 0.55, 0.15));
    c = mix(vec3(l), c, uSat / 0.75) * mix(0.72, 1.0, uDeep);
    float b = uBright * (0.22 + 1.3 * smoothstep(0.9, 2.2, crowd)) * (1.0 - 0.8 * dust);
    b *= 0.5 + 1.2 * fract(seed * 13.7) * fract(seed * 3.1);   // a spread of magnitudes
    // the beat's wave, centre -> outside
    float q = (r - uWaveR) / 0.07; b *= 1.0 + uWaveAmp * exp(-q * q);
    // the shock front: the pushed stars light up as it passes
    b *= 1.0 + 14.0 * push;
    vCol = c * b;
    float size = 1.0 + 2.0 * pow(fract(seed * 5.77), 6.0);     // mostly 1 px, a few up to 3
    gl_PointSize = size * uPxScale;
    // a sub-pixel point keeps 1 px and loses intensity (R1/R7)
    vCol *= size / max(size, 1.0);
  }
`;
const starFragment = `
  precision mediump float;
  varying vec3 vCol;
  uniform float uGain;
  void main() {
    vec2 q = gl_PointCoord - 0.5;
    float f = 1.0 - smoothstep(0.2, 0.5, length(q));
    gl_FragColor = vec4(vCol * uGain * f, 1.0);
  }
`;

const nebulaVertex = `
  precision highp float;
  ${GALAXY_GLSL}
  attribute vec4 aNeb;                  // a, arm index, angle jitter, seed
  uniform float uPxH, uNebBright, uNebArm, uNebFlash, uSat, uDeep;
  uniform vec3 uPink, uBlue;
  varying vec3 vCol;
  // the crest of arm j for m arms, at semi-axis a: dr/da is minimal at psi = pi + atan(a m k)
  float crest(float a, float m, float j) {
    float rot = kWind() * a + uPattern;
    return rot + (3.14159265 + atan(a * m * kWind())) / m + 6.2831853 * j / m;
  }
  void main() {
    float a = aNeb.x, j = aNeb.y, seed = aNeb.w;
    float c1 = crest(a, uM1, j), c2 = crest(a, uM2, j);
    // the shorter way round between the two crests
    float dc = mod(c2 - c1 + 3.14159265, 6.2831853) - 3.14159265;
    float th = c1 + dc * uMix + aNeb.z;
    float vis = mix(step(j, uM1 - 0.5), step(j, uM2 - 0.5), uMix);
    // scattered off the crest (an evenly spaced row read as beads), more toward the rim
    float r = ovalR(a, th) * (1.0 + (fract(seed * 6.1) - 0.5) * 0.12); r += shock(r);
    vec3 g = vec3(r * cos(th), r * sin(th), 0.0);
    gl_Position = project(g);
    vec3 col = mix(uPink, uBlue, step(0.5, fract(seed * 4.3)));
    float l = dot(col, vec3(0.3, 0.55, 0.15));
    col = mix(vec3(l), col, uSat / 0.75) * mix(0.7, 1.0, uDeep);
    float flash = uNebFlash * (1.0 - step(0.5, abs(j - uNebArm)));
    vCol = col * uNebBright * vis * (0.25 + 0.75 * pow(fract(seed * 9.1), 2.0)) * (1.0 + 1.6 * flash);
    // 0.035 to 0.08 of the disc, in pixels (perspective included)
    float size = (0.05 + 0.08 * fract(seed * 2.7)) * uScale * uPxH * gl_Position.w;
    gl_PointSize = size * (1.0 + 0.2 * flash);
  }
`;
const nebulaFragment = `
  precision mediump float;
  varying vec3 vCol;
  uniform float uGain;
  void main() {
    vec2 q = gl_PointCoord - 0.5;
    // a gaussian that is 0 well before the sprite's edge (R12); translucent (R14)
    float d = exp(-dot(q, q) / 0.018) - exp(-0.25 / 0.018);
    gl_FragColor = vec4(vCol * uGain * max(d, 0.0), 1.0);
  }
`;

const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const fadeFragment = `
  precision mediump float;
  uniform float uDecay;
  void main() { gl_FragColor = vec4(vec3(uDecay), 1.0); }
`;
const displayFragment = `
  precision highp float;
  uniform sampler2D uAcc;
  uniform float uAspect;
  uniform vec2 uStage;
  uniform vec2 uCore;                  // the projected galaxy centre, height units
  uniform float uScale, uBulge, uFlash, uTilt;
  uniform vec3 uBulgeCol;
  uniform vec2 uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  void main() {
    vec3 col = texture2D(uAcc, vUv).rgb;
    vec2 xy = (vUv - 0.5) * vec2(uAspect, 1.0);
    // the bulge's glow: an ellipse flattened by the tilt
    vec2 d = (xy - uCore) / (uScale * vec2(1.0, mix(1.0, 0.55, sin(uTilt))));
    float g = exp(-dot(d, d) / 0.006) * 0.55 + exp(-dot(d, d) / 0.04) * 0.25;
    col += uBulgeCol * g * (uBulge + 1.4 * uFlash);
    // the cover's mask (R19: it follows the cover)
    vec2 s2 = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 q = abs(s2 - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    col *= mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));
    // a dither: the wide bulge glow on near-black banded in 8 bits
    col += (fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) / 255.0;
    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;

export class GalaxyVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._count = TIER[this._tier] || TIER[1];
    const arms = parseInt(urlParam('galaxyarms'), 10);
    this._forceArms = arms >= 2 && arms <= 4 ? arms : null;
    const w = parseFloat(urlParam('galaxywind'));
    this._forceWind = Number.isFinite(w) ? w : null;
    this._adapt = new Adapt();
    this._vdjPost = new VdjPost({ bloomStrength: 0.45, bloomThreshold: 0.8, bloomRadius: 0.5, vignette: 0.3, grain: 0.0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
  }

  get postProcessing() { return this._vdjPost.value; }

  init(scene) {
    this._scene = scene;
    const shared = {
      uTime: { value: 0 }, uPattern: { value: 0 }, uWind: { value: 0.3 }, uEcc: { value: 0.7 },
      uM1: { value: 2 }, uM2: { value: 2 }, uMix: { value: 0 },
      uTilt: { value: 0.96 }, uDist: { value: 4 }, uScale: { value: 0.4 }, uAspect: safeZoneUniforms.uAspect,
      uStage: { value: new THREE.Vector2() }, uShockR: { value: -1 }, uShockAmp: { value: 0 },
      uSat: { value: 0.65 }, uDeep: { value: 0 }, uGain: { value: 0.2 }
    };
    this._shared = shared;
    // THE STARS: static attributes only
    const n = this._count, star = new Float32Array(n * 4);
    let s = 9301;
    const rnd = () => { s = (s * 16807) % 2147483647; return s / 2147483647; };
    const gauss = () => { let u = 0; for (let k = 0; k < 4; k++) u += rnd(); return (u - 2) * 1.73; };
    for (let i = 0; i < n; i++) {
      // 14 % bulge, the rest an exponential disc (scale length 0.3), cut at 1
      let a;
      if (rnd() < 0.14) a = 0.17 * Math.pow(rnd(), 1.6);
      else { do { a = 0.05 - 0.3 * Math.log(1 - rnd()); } while (a > 1); }
      star[i * 4] = a; star[i * 4 + 1] = rnd() * Math.PI * 2; star[i * 4 + 2] = gauss(); star[i * 4 + 3] = rnd();
    }
    const sg = new THREE.BufferGeometry();
    sg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    sg.setAttribute('aStar', new THREE.BufferAttribute(star, 4));
    this._starU = Object.assign({}, shared, {
      uPxScale: { value: 1 }, uBright: { value: 1 }, uWaveR: { value: -1 }, uWaveAmp: { value: 0 },
      uArmCol: { value: new THREE.Color(0x6a7dff) }, uGain: { value: 0.2 }
    });
    this._starMat = new THREE.ShaderMaterial({ uniforms: this._starU, vertexShader: starVertex, fragmentShader: starFragment,
      blending: THREE.AdditiveBlending, transparent: true, depthTest: false, depthWrite: false });
    this._stars = new THREE.Points(sg, this._starMat); this._stars.frustumCulled = false;
    // THE NEBULAE: on the arm crests, arm index 0..3
    const neb = new Float32Array(NEBULAE * 4);
    for (let i = 0; i < NEBULAE; i++) {
      neb[i * 4] = 0.2 + 0.72 * Math.sqrt(rnd()); neb[i * 4 + 1] = i % 4; neb[i * 4 + 2] = (rnd() - 0.5) * 0.6 * rnd(); neb[i * 4 + 3] = rnd();
    }
    const ng = new THREE.BufferGeometry();
    ng.setAttribute('position', new THREE.BufferAttribute(new Float32Array(NEBULAE * 3), 3));
    ng.setAttribute('aNeb', new THREE.BufferAttribute(neb, 4));
    this._nebU = Object.assign({}, shared, {
      uPxH: { value: 720 }, uNebBright: { value: 0.3 }, uNebArm: { value: -9 }, uNebFlash: { value: 0 },
      uPink: { value: new THREE.Color(0xff5fa8) }, uBlue: { value: new THREE.Color(0x4f7dff) }, uGain: { value: 0.2 }
    });
    this._nebMat = new THREE.ShaderMaterial({ uniforms: this._nebU, vertexShader: nebulaVertex, fragmentShader: nebulaFragment,
      blending: THREE.AdditiveBlending, transparent: true, depthTest: false, depthWrite: false });
    this._nebulae = new THREE.Points(ng, this._nebMat); this._nebulae.frustumCulled = false;
    // the accumulation's fade: dst *= decay
    this._fadeU = { uDecay: { value: 0 } };
    this._fadeMat = new THREE.ShaderMaterial({ uniforms: this._fadeU, vertexShader: quadVertex, fragmentShader: fadeFragment,
      blending: THREE.CustomBlending, blendEquation: THREE.AddEquation, blendSrc: THREE.ZeroFactor, blendDst: THREE.SrcColorFactor,
      transparent: true, depthTest: false, depthWrite: false });
    const fade = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._fadeMat); fade.frustumCulled = false; fade.renderOrder = 0;
    this._nebulae.renderOrder = 1; this._stars.renderOrder = 2;
    this._priv = new THREE.Scene();
    this._priv.add(fade, this._nebulae, this._stars);
    this._fadeQuad = fade;
    this._ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 10); this._ortho.position.z = 1;
    this._rt = new THREE.WebGLRenderTarget(4, 4, { type: THREE.HalfFloatType, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter,
      depthBuffer: false, stencilBuffer: false, generateMipmaps: false });
    // THE DISPLAY
    this._u = {
      uAcc: { value: this._rt.texture }, uAspect: safeZoneUniforms.uAspect, uStage: shared.uStage,
      uCore: { value: new THREE.Vector2() }, uScale: shared.uScale, uBulge: { value: 0.35 }, uFlash: { value: 0 }, uTilt: shared.uTilt,
      uBulgeCol: { value: new THREE.Color(1.0, 0.72, 0.42) },
      uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._displayMat = new THREE.ShaderMaterial({ uniforms: this._u, vertexShader: quadVertex, fragmentShader: displayFragment, depthTest: false, depthWrite: false });
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._displayMat);
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._size = new THREE.Vector2();
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    // state
    this._time = 0; this._clock = 0; this._pattern = 0; this._camPhase = 0.29;   // starts at 55 deg
    this._m1 = this._forceArms || 2; this._m2 = this._m1; this._mix = 0; this._mixDur = 1;
    this._armsCycle = [2, 3, 4]; this._armsIdx = 0;
    this._barCount = 0; this._nebFlash = 0; this._nebArm = -9;
    this._shockT = -1; this._flash = 0; this._dt = 0; this._clear = true;
    this._intensity = 0.3; this._chroma = 1;
    this._paletteRef = undefined;
  }

  update(deltaTime, profile) {
    if (profile.artworkPalette !== this._paletteRef) {
      this._paletteRef = profile.artworkPalette;
      const v = profile.artworkPalette && profile.artworkPalette.vividColors && profile.artworkPalette.vividColors.length ? profile.artworkPalette.vividColors : FALLBACK;
      // the brief's colours, tinted (25-30 %) by the cover
      const tint = new THREE.Color(v[0]), tint2 = new THREE.Color(v[1 % v.length]);
      this._starU.uArmCol.value.copy(tint);
      this._nebU.uPink.value.set(0xff4fa0).lerp(tint2, 0.3);
      this._nebU.uBlue.value.set(0x3f6dff).lerp(tint, 0.3);
    }
    const playing = profile.playbackState === undefined || profile.playbackState === 'PLAYING';
    const dt = playing ? Math.min(deltaTime, 0.1) : 0;
    this._dt = dt;
    const a = this._adapt.update(dt, profile);
    if (dt === 0) return;                      // paused: the sky freezes, never black
    this._time += dt;
    const I = a.intensity;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || null;
    const beatS = bpm ? 60 / bpm : 0.6;

    // THE CLOCKS: rotation and pattern speed follow motion (rate, not distance)
    const rate = 0.30 * a.motion;
    this._clock += dt * rate;
    this._pattern += dt * rate * 0.18;
    // the camera: 10 deg -> 80 deg over 128 beats (60 s without tempo)
    this._camPhase += dt * 2 * Math.PI / (bpm ? 128 * beatS : 60);

    // ARMS: a section changes the count (2 -> 3 -> 4 -> 2) in 2 beats
    if (this._mix >= 1) { this._m1 = this._m2; this._mix = 0; }
    if (a.section && !this._forceArms && this._m1 === this._m2) {
      this._armsIdx = (this._armsIdx + 1) % 3;
      this._m2 = this._armsCycle[this._armsIdx];
      this._mixDur = 2 * beatS;
    }
    if (this._m1 !== this._m2) this._mix = Math.min(1, this._mix + dt / this._mixDur);

    // the BAR: one arm's nebulae light up (at intensity)
    if (a.bar) { this._barCount++; this._nebArm = this._barCount % Math.max(this._m1, this._m2); this._nebFlash = smooth((I - 0.45) / 0.4); }
    this._nebFlash *= Math.pow(0.5, dt / (2 * beatS));

    // the DROP (only on a track that has drops): bulge flash + shock ring
    if (a.drop) { this._shockT = 0; this._flash = 1; }
    this._flash *= Math.pow(0.5, dt / 0.35);
    const sh = this._shared;
    if (this._shockT >= 0) {
      this._shockT += dt;
      const p = this._shockT / 1.6;              // across the disc in 1.6 s
      sh.uShockR.value = 0.05 + 1.15 * p;
      sh.uShockAmp.value = 0.13 * (1 - p) * smooth(this._shockT / 0.08);
      if (p >= 1) { this._shockT = -1; sh.uShockAmp.value = 0; sh.uShockR.value = -1; }
    }

    // the shape: loose and wide when calm, tight and sharp when intense
    sh.uTime.value = this._clock;
    sh.uPattern.value = this._pattern;
    sh.uWind.value = this._forceWind != null ? this._forceWind : 0.22 + 0.30 * I;
    sh.uEcc.value = 0.55 + 0.37 * I;
    sh.uM1.value = this._m1; sh.uM2.value = this._m2; sh.uMix.value = smooth(this._mix);
    sh.uTilt.value = (45 + 35 * Math.sin(this._camPhase)) * Math.PI / 180;
    sh.uSat.value = a.saturation;
    sh.uDeep.value = clamp01((a.saturation - 0.65) / 0.25);
    // the beat's wave: centre -> rim over the beat, amplitude = pulse
    this._starU.uWaveR.value = a.beatPhase * 1.3;
    this._starU.uWaveAmp.value = 1.2 * a.pulse;
    this._nebU.uNebBright.value = 0.05 + 0.07 * I;
    this._nebU.uNebArm.value = this._nebArm; this._nebU.uNebFlash.value = this._nebFlash;
    this._u.uBulge.value = 0.22 + 0.1 * I;
    this._u.uFlash.value = this._flash;
    // the vdj RGB split, on the beat, as strong as the pulse
    this._chroma = vdjBeatChroma(a.beatPhase, beatS);
    this._vdjPost.update(1 + (this._chroma - 1) * a.pulse);
    this._intensity = I;
  }

  // the frame: the projection fitted to the frame (R6), then fade + nebulae + stars into the accumulation
  _render(renderer) {
    renderer.getDrawingBufferSize(this._size);
    const W = Math.max(2, this._size.x), H = Math.max(2, this._size.y);
    if (this._rt.width !== W || this._rt.height !== H) { this._rt.setSize(W, H); this._clear = true; }
    const sh = this._shared, asp = safeZoneUniforms.uAspect.value || W / H;
    const st = safeZoneUniforms.uStageCenter.value;
    sh.uStage.value.set(st.x * 0.5, st.y * 0.5);
    // fit: the rim projected at this tilt (perspective included) stays inside 90 % of the frame
    const t = sh.uTilt.value, ct = Math.cos(t), stt = Math.sin(t), D = sh.uDist.value;
    let mx = 0, my = 0;
    for (let k = 0; k < 32; k++) {
      const th = k / 32 * Math.PI * 2, x = FIT_R * Math.cos(th), y = FIT_R * Math.sin(th);
      const vy = y * ct, vz = y * stt, w = D / (D - vz);
      mx = Math.max(mx, Math.abs(x * w)); my = Math.max(my, Math.abs(vy * w));
    }
    sh.uScale.value = Math.min(0.5 * asp * 0.9 / mx, 0.5 * 0.9 / Math.max(my, 0.05));
    this._u.uCore.value.copy(sh.uStage.value);
    const pxScale = Math.max(1, H / 1080);
    this._starU.uPxScale.value = pxScale;
    this._nebU.uPxH.value = H;
    // the stars' brightness does not depend on the tier: fewer stars, each brighter
    this._starU.uBright.value = 0.55 * 80000 / this._count;
    const decay = this._dt > 0 ? Math.pow(0.5, this._dt / HALF_LIFE) : 1;
    this._fadeU.uDecay.value = this._clear ? 0 : decay;
    const add = this._clear ? 1 : 1 - decay;
    this._starU.uGain.value = add; this._nebU.uGain.value = add;
    this._clear = false;
    const prev = renderer.getRenderTarget(), auto = renderer.autoClear;
    renderer.autoClear = false;
    renderer.setRenderTarget(this._rt);
    if (add > 0) renderer.render(this._priv, this._ortho);
    renderer.setRenderTarget(prev);
    renderer.autoClear = auto;
  }

  get probe() {
    const a = this._adapt.get(), sh = this._shared;
    return {
      t: +this._time.toFixed(2), intensity: +a.intensity.toFixed(2), pulse: +a.pulse.toFixed(2), cut: a.cutPolicy, colour: a.colorMode,
      arms: [this._m1, this._m2, +sh.uMix.value.toFixed(2)], wind: +sh.uWind.value.toFixed(2), ecc: +sh.uEcc.value.toFixed(2),
      tilt: +(sh.uTilt.value * 180 / Math.PI).toFixed(1), scale: +sh.uScale.value.toFixed(3), stars: this._count,
      shock: +sh.uShockAmp.value.toFixed(3), flash: +this._flash.toFixed(2), neb: [this._nebArm, +this._nebFlash.toFixed(2)], sim: this._adapt.simulated
    };
  }

  dispose() {
    if (this._display) { this._display.onBeforeRender = null; if (this._scene) this._scene.remove(this._display); this._display.geometry.dispose(); this._displayMat.dispose(); }
    if (this._stars) { this._stars.geometry.dispose(); this._starMat.dispose(); }
    if (this._nebulae) { this._nebulae.geometry.dispose(); this._nebMat.dispose(); }
    if (this._fadeQuad) { this._fadeQuad.geometry.dispose(); this._fadeMat.dispose(); }
    if (this._rt) this._rt.dispose();
    if (this._adapt) this._adapt.dispose();
    this._scene = null;
  }
}
